// YouTubeTimestampNode.jsx
import { ElementNode, $applyNodeReplacement } from "lexical";
import { playYoutubeAt, ytSecondsFromUrl } from "./youtubePlayer";

// Play the editor video whose id matches the button's data-url (pausing all others)
// and scroll that exact video into view.
function seekEditorVideo(fromButtonEl, url, seconds) {
    // Scope to the same editor instance the button lives in
    const root = fromButtonEl.closest(".editor-input") || document;
    const iframe = playYoutubeAt(root, url, seconds);

    // video isn't embedded in this editor → open the link instead
    if (!iframe) {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        return;
    }

    // scroll the wrapper (title bar + player) so the whole card is visible
    const target = iframe.closest(".iframe_node_wrapper") || iframe;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
}

export class YouTubeTimestampNode extends ElementNode {
    __url;
    __time;

    static getType() {
        return "youtube-timestamp";
    }

    static clone(node) {
        return new YouTubeTimestampNode(node.__url, node.__time, node.__key);
    }

    constructor(url = "", time = "", key) {
        super(key);
        this.__url = url;
        this.__time = time;
    }

    // inline so it sits next to heading/paragraph text
    isInline() {
        return true;
    }


    // ── render inside the editor ──────────────────────────────
    createDOM() {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "youtube_timestamp";
        btn.contentEditable = "false";
        btn.dataset.url = this.__url;
        btn.textContent = this.__time;

        btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                // read from the DOM so updateDOM changes are respected (closure `this` can be stale)
                const url = btn.dataset.url || "";
                const seconds = ytSecondsFromUrl(url, btn.textContent);
                seekEditorVideo(btn, url, seconds);
            } catch (err) {
                console.error("YouTubeTimestampNode click failed:", err);
            }
        });

        return btn;
    }

    updateDOM(prevNode, dom) {
        if (prevNode.__time !== this.__time) {
            dom.textContent = this.__time;
        }
        if (prevNode.__url !== this.__url) {
            dom.dataset.url = this.__url;
        }
        return false;
    }

    // ── import: match the buttons coming from markdown/HTML ──
    static importDOM() {
        return {
            button: (domNode) => {
                const isTimestamp =
                    domNode.hasAttribute?.("data-url") ||
                    domNode.classList?.contains("youtube_timestamp") ||
                    (domNode.getAttribute?.("className") || "").includes("youtube_timestamp");
                if (!isTimestamp) return null;

                return {
                    conversion: (node) => {
                        const url = node.getAttribute("data-url") || "";
                        const time = (node.textContent || "").trim();
                        if (!url || !time) return null;
                        return {
                            node: new YouTubeTimestampNode(url, time),
                            // 👇 drop the inner "06:37" text node so it isn't appended as a child
                            after: () => [],
                        };
                    },
                    priority: 1,
                };
            },
        };
    }
    // ── export back to clean HTML (note: real `class`) ───────
    exportDOM() {
        const btn = document.createElement("button");
        btn.setAttribute("type", "button");
        btn.setAttribute("class", "youtube_timestamp");
        btn.setAttribute("data-url", this.__url);
        btn.textContent = this.__time;
        return { element: btn };
    }

    // ── JSON persistence (survives save/reload) ──────────────
    static importJSON(serializedNode) {
        return new YouTubeTimestampNode(serializedNode.url, serializedNode.time);
    }



    exportJSON() {
        return {
            type: "youtube-timestamp",
            version: 1,
            url: this.__url,
            time: this.__time,
            children: [], // mirrors your IframeNode
        };
    }
}

export function $createYouTubeTimestampNode(url = "", time = "") {
    return $applyNodeReplacement(new YouTubeTimestampNode(url, time));
}

export function $isYouTubeTimestampNode(node) {
    return node instanceof YouTubeTimestampNode;
}